import React from 'react';
import { WizardState } from '../../../hooks/useZakatWizard';
import { Input } from '../../ui/Input';
import { Card } from '../../ui/Card';
import { Info } from 'lucide-react';
interface RetirementStepProps {
  state: WizardState;
  updateState: (updates: Partial<WizardState>) => void;
}
const displayVal = (v: number) => v === 0 ? '' : v;
export function RetirementStep({
  state,
  updateState
}: RetirementStepProps) {
  return <div className="space-y-6" data-id="element-444">
      <div className="mb-6" data-id="element-445">
        <h2 className="text-2xl font-bold text-gray-900 font-serif mb-2" data-id="element-446">
          Retirement Accounts
        </h2>
        <p className="text-gray-600" data-id="element-447">
          Retirement accounts have special considerations. Enter the current
          balance—we'll help you calculate the zakatable portion.
        </p>
      </div>

      <Card className="space-y-6" data-id="element-448">
        <div className="space-y-4 mb-8" data-id="element-449">
          <label className="block text-sm font-medium text-gray-700" data-id="element-450">
            Calculation Method
          </label>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3 text-sm text-blue-800" data-id="element-451">
            <Info className="h-5 w-5 flex-shrink-0 mt-0.5" data-id="element-452" />
            <p data-id="element-453">
              Scholars differ on how to treat retirement funds for Zakat. The
              key question is whether you truly "own" money that you cannot
              access without penalty.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-454">
            <button onClick={() => updateState({
            retirementOption: 'accessible'
          })} className={`p-4 rounded-lg border-2 text-left transition-all ${state.retirementOption === 'accessible' ? 'border-[#2D9596] bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-455">
              <div className="flex items-start justify-between gap-2" data-id="element-456">
                <div className="font-semibold text-gray-900 mb-1" data-id="element-457">
                  Accessible Balance (Standard)
                </div>
                <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${state.retirementOption === 'accessible' ? 'border-[#2D9596] bg-[#2D9596]' : 'border-gray-300'}`} data-id="element-458">
                  {state.retirementOption === 'accessible' && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-459">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-460" />
                    </svg>}
                </div>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed" data-id="element-461">
                Zakat is only due on the portion reachable without penalty. This
                view holds that ownership of the asset has not been "perfected"
                as long as accessing it triggers a penalty.
              </p>
            </button>

            <button onClick={() => updateState({
            retirementOption: 'full'
          })} className={`p-4 rounded-lg border-2 text-left transition-all ${state.retirementOption === 'full' ? 'border-[#2D9596] bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-462">
              <div className="flex items-start justify-between gap-2" data-id="element-463">
                <div className="font-semibold text-gray-900 mb-1" data-id="element-464">
                  Full Balance (Maximally Conservative)
                </div>
                <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${state.retirementOption === 'full' ? 'border-[#2D9596] bg-[#2D9596]' : 'border-gray-300'}`} data-id="element-465">
                  {state.retirementOption === 'full' && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-466">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-467" />
                    </svg>}
                </div>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed" data-id="element-468">
                Calculates Zakat on 100% of the account balance. This approach
                is taken by those who wish to be maximally cautious, treating
                the entire fund as fully owned regardless of withdrawal
                penalties.
              </p>
            </button>
          </div>
        </div>

        <div className="space-y-6" data-id="element-469">
          <Input label="401(k) / 403(b) Balance" prefix="$" type="number" placeholder="0.00" value={displayVal(state.retirement401k)} onChange={e => updateState({
          retirement401k: parseFloat(e.target.value) || 0
        })} data-id="element-470" />

          <Input label="Traditional IRA" prefix="$" type="number" placeholder="0.00" value={displayVal(state.retirementIra)} onChange={e => updateState({
          retirementIra: parseFloat(e.target.value) || 0
        })} data-id="element-471" />

          <Input label="Roth IRA" prefix="$" type="number" placeholder="0.00" value={displayVal(state.retirementRoth)} onChange={e => updateState({
          retirementRoth: parseFloat(e.target.value) || 0
        })} data-id="element-472" />

          <Input label="HSA (Health Savings Account)" prefix="$" type="number" placeholder="0.00" value={displayVal(state.retirementHsa)} onChange={e => updateState({
          retirementHsa: parseFloat(e.target.value) || 0
        })} data-id="element-473" />

          <Input label="Other Retirement Plans" prefix="$" type="number" placeholder="0.00" value={displayVal(state.retirementOther)} onChange={e => updateState({
          retirementOther: parseFloat(e.target.value) || 0
        })} data-id="element-474" />
        </div>
      </Card>
    </div>;
}